"use client";

import * as React from "react";

import { cn } from "@/lib/utils";
import { Link, LinkProps } from "@tanstack/react-router";

function Table({
  className,
  ...props
}: React.ComponentProps<"table"> & { stickyHeader?: boolean }) {
  return (
    <div
      data-slot="table-container"
      className={cn({ "relative w-full overflow-x-auto": !props.stickyHeader })}
    >
      <div
        data-slot="table"
        className={cn("table w-full caption-bottom text-sm", className)}
        {...props}
      />
    </div>
  );
}

function TableHeader({
  className,
  ...props
}: React.ComponentProps<"thead"> & { sticky?: boolean }) {
  return (
    <div
      data-slot="table-header"
      className={cn(
        "table-row-group [&_tr]:border-b",
        {
          "bg-background-1 shadow-foreground/5 sticky top-0 shadow-md":
            props.sticky,
        },
        className,
      )}
      {...props}
    />
  );
}

function TableBody({ className, ...props }: React.ComponentProps<"tbody">) {
  return (
    <div
      data-slot="table-body"
      className={cn("table-row-group [&_tr:last-child]:border-0", className)}
      {...props}
    />
  );
}

function TableFooter({ className, ...props }: React.ComponentProps<"tfoot">) {
  return (
    <div
      data-slot="table-footer"
      className={cn(
        "table-footer bg-muted/50 border-t font-medium [&>tr]:last:border-b-0",
        className,
      )}
      {...props}
    />
  );
}

const TableRow = React.forwardRef<
  HTMLTableRowElement | HTMLAnchorElement,
  React.PropsWithChildren<{
    className?: string;
    link?: LinkProps | undefined;
    onClick?: () => void;
  }>
>(({ className, link, children, ...rest }, ref) => {
  const commonClassname = cn(
    "table-row",
    "data-[state=selected]:bg-muted border-b transition-colors",
    className,
  );
  if (link) {
    return (
      <Link
        ref={ref as React.Ref<HTMLAnchorElement>}
        className={cn(
          "hover:bg-accent-secondary-2 cursor-pointer",
          commonClassname,
        )}
        {...link}
      >
        {children}
      </Link>
    );
  }

  return (
    <div
      ref={ref as React.Ref<HTMLDivElement>}
      className={cn("hover:bg-muted/50", commonClassname)}
      {...rest}
    >
      {children}
    </div>
  );
});

function TableHead({ className, ...props }: React.ComponentProps<"th">) {
  return (
    <div
      data-slot="table-head"
      className={cn(
        "table-cell",
        "text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
        className,
      )}
      {...props}
    />
  );
}

function TableCell({ className, ...props }: React.ComponentProps<"td">) {
  return (
    <div
      data-slot="table-cell"
      className={cn(
        "table-cell",
        "p-2 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
        className,
      )}
      {...props}
    />
  );
}

function TableCaption({
  className,
  ...props
}: React.ComponentProps<"caption">) {
  return (
    <caption
      data-slot="table-caption"
      className={cn("text-muted-foreground mt-4 text-sm", className)}
      {...props}
    />
  );
}

const TableHeadButton = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> & { active?: boolean }
>(({ className, ...props }, ref) => {
  return (
    <button
      ref={ref}
      data-slot="table-head-button"
      className={cn(
        "inline-flex cursor-pointer items-center gap-2",
        { "font-bold": props.active },
        className,
      )}
      {...props}
    />
  );
});
TableHeadButton.displayName = "TableHeadButton";

// ts-unused-exports:disable-next-line
export {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeadButton,
  TableHeader,
  TableRow,
};
